import { useState } from 'react';
import { toast } from 'react-toastify';
import { ModalContent } from '@components/modal';
import HelpRequestSummary from './HelpRequestSummary';
import { useModal } from '@stores';
import { ResponseStatuses, HelpRequestStatuses, UserPermissions } from '@src/utils/enums';
import { useHelpInquirySummaryQuery, useUpdateHelpInquiryStatusMutation } from '@api/services/help/hooks';
import { UpdateHelpInquiryStatus } from '@api/services/help/types';
import { useQueryClient } from '@tanstack/react-query';
import { DASHBOARD_QUERY_KEYS } from '@api/services/dashboard/query';
import { HELP_QUERY_KEYS } from '@api/services/help/query';
import Loader from '@components/loader/Loader';
import LoaderButton from '@components/loader/LoaderButton';
import { useUserRoleCapabilities } from '@stores/userRoleCapabilities.store';
import './helpRequestSummary.scss';

type HelpRequestSummaryModalProps = {
  requestId: number;
  version: number;
  helpInquiryId: number;
};

type HelpInquiryAction = {
  status: UpdateHelpInquiryStatus;
  label: string;
  pendingLabel: string;
  secondary?: boolean;
  title: string;
};

const claimHelpRequestAction: HelpInquiryAction = {
  status: HelpRequestStatuses.InProgress,
  label: 'Claim Help Inquiry',
  pendingLabel: 'Claiming...',
  title: 'Assign this help inquiry to yourself to indicate you are actively working on it.',
};

const releaseHelpRequestAction: HelpInquiryAction = {
  status: HelpRequestStatuses.Open,
  label: 'Unclaim Help Inquiry',
  pendingLabel: 'Unclaiming...',
  secondary: true,
  title: 'Unassign this help inquiry from yourself to make it available for others.',
};

const closeHelpRequestAction: HelpInquiryAction = {
  status: HelpRequestStatuses.Completed,
  label: 'Complete Help Inquiry',
  pendingLabel: 'Completing...',
  title: 'Complete this help inquiry to indicate it has been resolved.',
};

const getHelpInquiryActions = ({
  canClaimHelpRequest,
  canManageAssignedHelpRequest,
}: {
  canClaimHelpRequest: boolean;
  canManageAssignedHelpRequest: boolean;
}): HelpInquiryAction[] => {
  if (canClaimHelpRequest) {
    return [claimHelpRequestAction];
  }

  if (canManageAssignedHelpRequest) {
    return [releaseHelpRequestAction, closeHelpRequestAction];
  }

  return [];
};

const HelpRequestSummaryModal = ({ requestId, version, helpInquiryId }: HelpRequestSummaryModalProps) => {
  const { closeModal } = useModal();
  const [pendingStatus, setPendingStatus] = useState<UpdateHelpInquiryStatus | null>(null);

  const { data: summary, isLoading } = useHelpInquirySummaryQuery({
    requestId,
    version,
    helpInquiryId,
  });

  const queryClient = useQueryClient();
  const { mutate: updateHelpInquiry, isPending } = useUpdateHelpInquiryStatusMutation({
    onSuccess: (response) => {
      const { msg, status } = response;

      if (status !== ResponseStatuses.Success) {
        setPendingStatus(null);
        toast.error(msg || 'Unable to update help inquiry.');
        return;
      }
      toast.success(msg);
      closeModal();
      void queryClient.invalidateQueries({
        queryKey: [DASHBOARD_QUERY_KEYS.GET_REGULATOR_HELP_REQUESTS],
      });
      void queryClient.invalidateQueries({
        queryKey: [HELP_QUERY_KEYS.GET_HELP_INQUIRY_SUMMARY, requestId, version, helpInquiryId],
        refetchType: 'none',
      });
    },
    onError: () => {
      setPendingStatus(null);
      toast.error('Unable to update help inquiry.');
    },
  });

  const canProcessHelpInquiry = useUserRoleCapabilities.getState().hasRoleCapability('processHelpInquiry');
  const isOpen = summary?.status === HelpRequestStatuses.Open;
  const isInProgress = summary?.status === HelpRequestStatuses.InProgress;
  const isSelfAssigned = summary?.permission === UserPermissions.ReadOnly;
  const canClaimHelpRequest = canProcessHelpInquiry && isOpen;
  const canManageAssignedHelpRequest = canProcessHelpInquiry && isInProgress && isSelfAssigned;

  const helpInquiryActions = getHelpInquiryActions({
    canClaimHelpRequest,
    canManageAssignedHelpRequest,
  });

  const handleUpdateHelpRequest = (action: HelpInquiryAction) => {
    setPendingStatus(action.status);

    updateHelpInquiry({
      requestId,
      version,
      helpInquiryId,
      status: action.status,
    });
  };

  return (
    <ModalContent title='Help Inquiry Summary' hasCloseButton={true}>
      <div className='modal-body'>
        {helpInquiryActions.length > 0 && (
          <div className='help-request-summary__claim-button-container'>
            {helpInquiryActions.map((action) => (
              <LoaderButton
                key={action.status}
                className='button-small'
                outline={action.secondary}
                type='button'
                isLoading={isPending && pendingStatus === action.status}
                isDisabled={isPending}
                onClick={() => handleUpdateHelpRequest(action)}
                title={action.title}
              >
                {isPending && pendingStatus === action.status ? action.pendingLabel : action.label}
              </LoaderButton>
            ))}
          </div>
        )}

        {isLoading && <Loader variant='modal' loaderText='Loading help inquiry summary...' />}
        {summary && <HelpRequestSummary summary={summary} />}
      </div>
    </ModalContent>
  );
};

export default HelpRequestSummaryModal;
